import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  People as PeopleIcon,
  PersonAdd as PersonAddIcon,
  AdminPanelSettings as AdminIcon,
  SupervisorAccount as ManagerIcon,
  Badge as EmployeeIcon,
  LinkOff as UnlinkIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import api from "../services/api";
import TableLoadingState from "../components/ui/TableLoadingState";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { useFormik } from "formik";

/** API may expose Mongo id as `_id` or `id`; missing field must not be sent as undefined (JSON drops it). */
function userId(u) {
  if (!u) return null;
  const v = u._id ?? u.id;
  return v != null ? String(v) : null;
}

export default function RoleManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assignRoleDialogOpen, setAssignRoleDialogOpen] = useState(false);
  const [assignEmployeesDialogOpen, setAssignEmployeesDialogOpen] =
    useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [managers, setManagers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [removeReportsToDialogOpen, setRemoveReportsToDialogOpen] = useState(false);
  const [userToUnassign, setUserToUnassign] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get("/users");
      const payload = response.data;
      const userData = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
        ? payload.data
        : [];
      setUsers(userData);

      // Separate managers and employees for assignment dialog
      setManagers(
        userData.filter((u) => u.role === "MANAGER" || u.role === "ADMIN")
      );
      setEmployees(userData.filter((u) => u.role === "EMPLOYEE"));
    } catch (err) {
      console.error("Failed to fetch users", err);
      toast.error(err.response?.data?.error || "Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  const assignRoleFormik = useFormik({
    initialValues: {
      role: "",
    },
    onSubmit: async (values) => {
      if (!selectedUser) return;
      const tid = userId(selectedUser);
      if (!tid) {
        toast.error("Could not resolve user id. Refresh and try again.");
        return;
      }

      try {
        await api.post("/users/assign-role", {
          targetUserId: tid,
          role: values.role,
        });
        toast.success("Role assigned successfully");
        setAssignRoleDialogOpen(false);
        setSelectedUser(null);
        assignRoleFormik.resetForm();
        fetchUsers();
      } catch (err) {
        toast.error(err.response?.data?.error || "Failed to assign role");
      }
    },
  });

  const assignEmployeesFormik = useFormik({
    initialValues: {
      managerId: "",
      employeeIds: [],
    },
    onSubmit: async (values) => {
      if (!values.managerId || values.employeeIds.length === 0) {
        toast.error("Please select a manager and at least one employee");
        return;
      }

      try {
        await api.post("/users/assign-employees", {
          managerId: values.managerId,
          employeeIds: values.employeeIds,
        });
        toast.success("Employees assigned successfully");
        setAssignEmployeesDialogOpen(false);
        assignEmployeesFormik.resetForm();
        fetchUsers();
      } catch (err) {
        toast.error(err.response?.data?.error || "Failed to assign employees");
      }
    },
  });

  const handleAssignRoleClick = (user) => {
    setSelectedUser(user);
    assignRoleFormik.setFieldValue("role", user.role || "");
    setAssignRoleDialogOpen(true);
  };

  const handleRemoveReportsToClick = (user) => {
    setUserToUnassign(user);
    setRemoveReportsToDialogOpen(true);
  };

  const handleRemoveReportsToConfirm = async () => {
    if (!userToUnassign) return;
    const tid = userId(userToUnassign);
    if (!tid) {
      toast.error("Could not resolve user id.");
      return;
    }
    try {
      await api.delete(`/users/${tid}/reports-to`);
      toast.success(`Removed "${userToUnassign.reportsTo?.name}" as manager for ${userToUnassign.name}`);
      setRemoveReportsToDialogOpen(false);
      setUserToUnassign(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to remove reports-to assignment");
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case "ADMIN":
        return <AdminIcon className="text-purple-600" />;
      case "MANAGER":
        return <ManagerIcon className="text-blue-600" />;
      case "HR":
        return <EmployeeIcon className="text-orange-500" />;
      case "EMPLOYEE":
        return <EmployeeIcon className="text-green-600" />;
      default:
        return <EmployeeIcon />;
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case "ADMIN":
        return "bg-purple-100 text-purple-800";
      case "MANAGER":
        return "bg-blue-100 text-blue-800";
      case "HR":
        return "bg-orange-100 text-orange-800";
      case "EMPLOYEE":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  if (loading) {
    return <TableLoadingState />;
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6 w-full min-w-0">
      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        className="max-w-7xl mx-auto w-full min-w-0"
      >
        {/* Header */}
        <motion.div variants={cardVariants} className="mb-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex flex-wrap items-center gap-2 sm:gap-3">
                <PeopleIcon className="text-purple-600 flex-shrink-0" />
                Role Management
              </h1>
              <p className="text-gray-600 mt-2 text-sm sm:text-base">
                Manage user roles and assign employees to managers
              </p>
            </div>
            <div className="flex gap-3 flex-shrink-0">
              <Button
                variant="contained"
                startIcon={<PersonAddIcon />}
                onClick={() => setAssignEmployeesDialogOpen(true)}
                className="bg-purple-600 hover:bg-purple-700"
              >
                Assign Employees
              </Button>
            </div>
          </div>
        </motion.div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-6 mb-6">
          <motion.div
            variants={cardVariants}
            className="bg-white rounded-lg shadow p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Total Users</p>
                <p className="text-3xl font-bold text-gray-900">
                  {users.length}
                </p>
              </div>
              <PeopleIcon className="text-purple-600 text-4xl" />
            </div>
          </motion.div>

          <motion.div
            variants={cardVariants}
            className="bg-white rounded-lg shadow p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Managers</p>
                <p className="text-3xl font-bold text-blue-600">
                  {users.filter((u) => u.role === "MANAGER").length}
                </p>
              </div>
              <ManagerIcon className="text-blue-600 text-4xl" />
            </div>
          </motion.div>

          <motion.div
            variants={cardVariants}
            className="bg-white rounded-lg shadow p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">HR</p>
                <p className="text-3xl font-bold text-orange-500">
                  {users.filter((u) => u.role === "HR").length}
                </p>
              </div>
              <EmployeeIcon className="text-orange-500 text-4xl" />
            </div>
          </motion.div>

          <motion.div
            variants={cardVariants}
            className="bg-white rounded-lg shadow p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Employees</p>
                <p className="text-3xl font-bold text-green-600">
                  {users.filter((u) => u.role === "EMPLOYEE").length}
                </p>
              </div>
              <EmployeeIcon className="text-green-600 text-4xl" />
            </div>
          </motion.div>
        </div>

        {/* Users Table — table-fixed + wrapped text avoids horizontal overflow in narrow main column */}
        <motion.div
          variants={cardVariants}
          className="bg-white rounded-lg shadow min-w-0"
        >
          <div className="w-full min-w-0">
            <table className="w-full table-fixed border-collapse divide-y divide-gray-200 text-sm">
              <colgroup>
                <col style={{ width: "30%" }} />
                <col style={{ width: "14%" }} />
                <col style={{ width: "20%" }} />
                <col style={{ width: "18%" }} />
                <col style={{ width: "18%" }} />
              </colgroup>
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-0">
                    User
                  </th>
                  <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-0">
                    Role
                  </th>
                  <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-0">
                    Reports To
                  </th>
                  <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-0">
                    Managed
                  </th>
                  <th className="px-2 sm:px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider min-w-0">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((user) => (
                  <tr key={userId(user) || user.email} className="hover:bg-gray-50">
                    <td className="px-2 sm:px-4 py-3 align-top min-w-0">
                      <div className="flex items-start gap-2 min-w-0">
                        <div className="flex-shrink-0 h-9 w-9 sm:h-10 sm:w-10 rounded-full bg-purple-100 flex items-center justify-center">
                          <span className="text-purple-600 font-semibold text-sm">
                            {user.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="min-w-0 pt-0.5">
                          <div className="font-medium text-gray-900 break-words">
                            {user.name}
                          </div>
                          <div className="text-gray-500 break-all text-xs sm:text-sm">
                            {user.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-2 sm:px-4 py-3 align-top min-w-0">
                      <div className="flex flex-wrap items-center gap-1 sm:gap-2">
                        <span className="flex-shrink-0">{getRoleIcon(user.role)}</span>
                        <Chip
                          label={user.role}
                          className={getRoleColor(user.role)}
                          size="small"
                          sx={{ maxWidth: "100%" }}
                        />
                      </div>
                    </td>
                    <td className="px-2 sm:px-4 py-3 text-gray-500 align-top min-w-0 break-words">
                      {user.reportsTo?.name ? (
                        <div className="flex flex-wrap items-start gap-1">
                          <span className="break-words">{user.reportsTo.name}</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveReportsToClick(user)}
                            title="Remove this reporting assignment"
                            className="p-0.5 rounded text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors flex-shrink-0"
                          >
                            <UnlinkIcon sx={{ fontSize: 14 }} />
                          </button>
                        </div>
                      ) : (
                        "--"
                      )}
                    </td>
                    <td className="px-2 sm:px-4 py-3 text-gray-500 align-top min-w-0 break-words text-xs sm:text-sm">
                      {user.managedUsers?.length || 0} direct report(s)
                    </td>
                    <td className="px-2 sm:px-4 py-3 text-sm font-medium align-top text-right min-w-0">
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleAssignRoleClick(user)}
                        sx={{ minWidth: 0, whiteSpace: "normal", lineHeight: 1.2 }}
                        className="!text-xs sm:!text-sm !px-2 !py-1"
                      >
                        Change Role
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* Assign Role Dialog */}
        <Dialog
          open={assignRoleDialogOpen}
          onClose={() => {
            setAssignRoleDialogOpen(false);
            setSelectedUser(null);
            assignRoleFormik.resetForm();
          }}
          maxWidth="sm"
          fullWidth
        >
          <form onSubmit={assignRoleFormik.handleSubmit}>
            <DialogTitle>Assign Role</DialogTitle>
            <DialogContent>
              {selectedUser && (
                <div className="mb-4">
                  <p className="text-sm text-gray-600">
                    User:{" "}
                    <span className="font-semibold">{selectedUser.name}</span>
                  </p>
                  <p className="text-sm text-gray-600">
                    Email:{" "}
                    <span className="font-semibold">{selectedUser.email}</span>
                  </p>
                </div>
              )}
              <FormControl fullWidth className="mt-4">
                <InputLabel>Role</InputLabel>
                <Select
                  name="role"
                  value={assignRoleFormik.values.role}
                  onChange={assignRoleFormik.handleChange}
                  label="Role"
                >
                  <MenuItem value="EMPLOYEE">Employee</MenuItem>
                  <MenuItem value="MANAGER">Manager</MenuItem>
                  <MenuItem value="HR">HR</MenuItem>
                  <MenuItem value="ADMIN">Admin</MenuItem>
                </Select>
              </FormControl>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  setAssignRoleDialogOpen(false);
                  setSelectedUser(null);
                  assignRoleFormik.resetForm();
                }}
              >
                Cancel
              </Button>
              <Button type="submit" variant="contained" color="primary">
                Assign
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        {/* Assign Employees Dialog */}
        <Dialog
          open={assignEmployeesDialogOpen}
          onClose={() => {
            setAssignEmployeesDialogOpen(false);
            assignEmployeesFormik.resetForm();
          }}
          maxWidth="md"
          fullWidth
        >
          <form onSubmit={assignEmployeesFormik.handleSubmit}>
            <DialogTitle>Assign Employees to Manager</DialogTitle>
            <DialogContent>
              <FormControl fullWidth className="mt-4">
                <InputLabel>Manager</InputLabel>
                <Select
                  name="managerId"
                  value={assignEmployeesFormik.values.managerId}
                  onChange={assignEmployeesFormik.handleChange}
                  label="Manager"
                >
                  {managers.map((manager) => (
                    <MenuItem key={userId(manager)} value={userId(manager)}>
                      {manager.name} ({manager.role})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <div className="mt-6">
                <p className="text-sm font-medium text-gray-700 mb-3">
                  Select Employees:
                </p>
                <div className="max-h-64 overflow-y-auto border border-gray-200 rounded p-3">
                  {employees.map((employee) => (
                    <FormControlLabel
                      key={userId(employee)}
                      control={
                        <Checkbox
                          checked={assignEmployeesFormik.values.employeeIds.includes(
                            userId(employee)
                          )}
                          onChange={(e) => {
                            const eid = userId(employee);
                            const currentIds =
                              assignEmployeesFormik.values.employeeIds;
                            if (e.target.checked) {
                              assignEmployeesFormik.setFieldValue(
                                "employeeIds",
                                [...currentIds, eid]
                              );
                            } else {
                              assignEmployeesFormik.setFieldValue(
                                "employeeIds",
                                currentIds.filter((id) => id !== eid)
                              );
                            }
                          }}
                        />
                      }
                      label={`${employee.name} (${employee.email})`}
                    />
                  ))}
                </div>
              </div>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  setAssignEmployeesDialogOpen(false);
                  assignEmployeesFormik.resetForm();
                }}
              >
                Cancel
              </Button>
              <Button type="submit" variant="contained" color="primary">
                Assign
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        {/* Remove Reports To Confirmation Dialog */}
        <Dialog
          open={removeReportsToDialogOpen}
          onClose={() => {
            setRemoveReportsToDialogOpen(false);
            setUserToUnassign(null);
          }}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Remove Reporting Assignment</DialogTitle>
          <DialogContent>
            {userToUnassign && (
              <p className="text-sm text-gray-600">
                Remove{" "}
                <span className="font-semibold">{userToUnassign.name}</span>{" "}
                from reporting to{" "}
                <span className="font-semibold">
                  {userToUnassign.reportsTo?.name}
                </span>
                ? This also removes them from that manager's list.
              </p>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setRemoveReportsToDialogOpen(false);
                setUserToUnassign(null);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleRemoveReportsToConfirm}
              color="error"
              variant="contained"
            >
              Remove
            </Button>
          </DialogActions>
        </Dialog>
      </motion.div>
    </div>
  );
}

